import { useState, useMemo, useEffect } from 'react';
import { useColorContext } from '../contexts/ColorContext';
import { SubstrateEditor } from '../components/SubstrateEditor';
import { SubstrateVisualization } from '../components/SubstrateVisualization';
import { SubstrateSelector } from '../components/SubstrateSelector';
import { LayerStackPanel } from '../components/LayerStackPanel';
import { SAMPLE_SUBSTRATE_PROFILES } from '../data/substrateProfiles';
import type { SubstrateProfile, LayerStack } from '../types/substrate';
import { Settings, Eye, Layers as LayersIcon } from 'lucide-react';
import { useToolInit } from '../hooks/useToolInit';

type SubstrateTab = 'preview' | 'editor' | 'layers';

export function SubstrateTool() {
  const { result, setInput } = useColorContext();
  const { params } = useToolInit();
  const [activeTab, setActiveTab] = useState<SubstrateTab>('preview');
  const [selectedSubstrate, setSelectedSubstrate] = useState<SubstrateProfile | null>(null);
  const [layerStack, setLayerStack] = useState<LayerStack | null>(null);

  // Deep-link: ?color= sets global active color
  useEffect(() => {
    if (params.color) setInput(params.color);
  }, [params.color, setInput]);

  const activeColor = result?.hex ?? '#3b82f6';

  // Default substrate for layer stack (use first sample or selected)
  const layerSubstrate = selectedSubstrate ?? SAMPLE_SUBSTRATE_PROFILES[0];

  const defaultLayerStack = useMemo<LayerStack>(() => ({
    id: 'default-stack',
    name: 'New Layer Stack',
    layers: [],
    substrate: layerSubstrate,
    totalInkCoverage: 0,
    metadata: { createdAt: new Date().toISOString(), version: '1.0.0' },
  }), [layerSubstrate]);

  const tabs: { id: SubstrateTab; label: string; icon: typeof Eye }[] = [
    { id: 'preview', label: 'Preview', icon: Eye },
    { id: 'layers', label: 'Layer Stack', icon: LayersIcon },
    { id: 'editor', label: 'Create Profile', icon: Settings },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
      <header style={{ marginBottom: '20px' }}>
        <h2 style={{
          fontSize: 'var(--t-h2, 20px)', fontWeight: 'var(--w-bold, 700)',
          color: 'var(--ink, #111)', marginBottom: '4px',
        }}>
          Substrate Simulator
        </h2>
        <p style={{ fontSize: 'var(--t-body, 13px)', color: 'var(--ink-2, #6b7280)' }}>
          Preview how colors render on different print substrates — paper, metallic, plastic, and fabric.
        </p>
      </header>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: '4px', padding: '3px',
        background: 'var(--panel-2, #f3f4f6)', borderRadius: 'var(--radius-lg, 8px)',
        width: 'fit-content', marginBottom: '20px',
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: 'var(--radius, 6px)',
                fontSize: '13px', fontWeight: 500, border: 'none', cursor: 'pointer',
                background: activeTab === tab.id ? 'var(--panel, #fff)' : 'transparent',
                color: activeTab === tab.id ? 'var(--ink, #111)' : 'var(--ink-3, #999)',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <SubstrateSelector
            onSelect={setSelectedSubstrate}
            selectedSubstrate={selectedSubstrate ?? undefined}
          />
          {selectedSubstrate && (
            <div style={{
              display: 'flex', gap: '24px', alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}>
              <SubstrateVisualization
                substrate={selectedSubstrate}
                color={activeColor}
                size="large"
                showTexture
              />
              <div style={{ flex: 1, minWidth: '240px' }}>
                <h3 style={{
                  fontSize: '16px', fontWeight: 600,
                  color: 'var(--ink, #111)', marginBottom: '8px',
                }}>
                  {selectedSubstrate.name}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--ink-2, #6b7280)', marginBottom: '12px' }}>
                  {selectedSubstrate.manufacturer}
                </p>
                <dl style={{
                  display: 'grid', gridTemplateColumns: 'auto 1fr',
                  gap: '4px 16px', fontSize: '13px',
                }}>
                  <dt style={{ color: 'var(--ink-3, #999)' }}>Category</dt>
                  <dd style={{ color: 'var(--ink, #111)', textTransform: 'capitalize' }}>{selectedSubstrate.category}</dd>
                  <dt style={{ color: 'var(--ink-3, #999)' }}>Brightness</dt>
                  <dd style={{ color: 'var(--ink, #111)' }}>{selectedSubstrate.brightness}%</dd>
                  <dt style={{ color: 'var(--ink-3, #999)' }}>Glossiness</dt>
                  <dd style={{ color: 'var(--ink, #111)' }}>{selectedSubstrate.glossiness}%</dd>
                  <dt style={{ color: 'var(--ink-3, #999)' }}>TAC Limit</dt>
                  <dd style={{ color: 'var(--ink, #111)' }}>{selectedSubstrate.tacLimit}%</dd>
                </dl>
              </div>
            </div>
          )}
          {!selectedSubstrate && (
            <div style={{
              padding: '48px 24px', textAlign: 'center',
              color: 'var(--ink-3, #999)', fontSize: '13px',
              border: '1px dashed var(--line, #e5e7eb)',
              borderRadius: 'var(--radius-xl, 12px)',
            }}>
              Select a substrate above to preview how <strong style={{ color: 'var(--ink, #111)', fontFamily: 'monospace' }}>{activeColor}</strong> renders on it.
            </div>
          )}
        </div>
      )}

      {activeTab === 'layers' && (
        <LayerStackPanel
          layerStack={layerStack ?? defaultLayerStack}
          substrate={layerSubstrate}
          onLayerStackChange={setLayerStack}
        />
      )}

      {activeTab === 'editor' && (
        <SubstrateEditor
          onSave={(substrate) => {
            setSelectedSubstrate(substrate);
            setActiveTab('preview');
          }}
          onCancel={() => setActiveTab('preview')}
        />
      )}
    </div>
  );
}
